/**
 * Every list surface. The header, then the table.
 *
 * The payload arrives from the resolver on first paint and from /list on every
 * filter and page change, so a narrowed table costs one query rather than a
 * re-resolution of the route. The query string is the state: a filtered,
 * sorted, paged table is a link somebody can send, and the browser's back
 * button walks it.
 */
import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { api } from '../api/client'
import type { ListPayload } from '../api/types'
import { DataList } from '../components/DataList'
import { PageHead } from '../components/PageHead'
import { Note, Spinner } from '../components/bits'

export function ListScreen({ initial }: { initial: ListPayload }) {
  const [params] = useSearchParams()
  const [data, setData] = useState(initial)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const query = params.toString()
  const sectionKey = initial.section.section_key

  useEffect(() => { setData(initial) }, [initial])

  useEffect(() => {
    // The first paint already has the payload the resolver returned, so an
    // immediate refetch of the same query would double every page load.
    if (!query) return
    let live = true
    setBusy(true)
    const fields: Record<string, string> = {}
    params.forEach((value, key) => { fields[key] = value })
    api.list(sectionKey, fields)
      .then((result) => { if (live) { setData(result.data.d); setError(null) } })
      .catch((e: Error) => { if (live) setError(e.message) })
      .finally(() => { if (live) setBusy(false) })
    return () => { live = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, sectionKey])

  return (
    <>
      <PageHead section={data.section} grant={data.grant} />
      {error && <Note kind="bad"><b>That filter could not be applied.</b> {error}</Note>}
      {busy && data.rows.length === 0 ? <Spinner /> : <DataList d={data} />}
    </>
  )
}
